import {
    DeleteOutlined,
    DownloadOutlined,
    EditOutlined,
    ForwardToInboxOutlined,
    MoreVert,
    VisibilityOutlined,
} from '@mui/icons-material';
import {
    CircularProgress,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import React, { useState, type MouseEvent } from 'react';

import Result from 'components/v2/common/Result';
import TableSkeleton from 'components/v2/common/tableSkeleton';

import { ACTIONS } from 'constant';
import {
    canDeleteAgreement,
    canOpenAgreementMilestones,
    canResendAgreement,
    isAgreementSigned,
    isAssessmentAgreement,
} from 'helpers/clientAgreements';
import { clientAgreementRowKey } from 'hooks/useClientAgreementRows';
import useTranslate from 'hooks/useTranslate';
import type { ClientAgreementRow } from 'types';
import { formatDate } from 'utils/formatDate';
import { formatNumber } from 'utils/formatting';

interface PdfAgreementsTableProps {
    rows: ClientAgreementRow[];
    loading: boolean;
    busyKey: string | null;
    handleAction: (action: ACTIONS, row?: ClientAgreementRow, rowKey?: string) => void;
}

const headCellSx = {
    fontWeight: 700,
    bgcolor: 'grey.50',
    whiteSpace: 'nowrap' as const,
};

const serviceLabel = (row: ClientAgreementRow, assessmentLabel: string) => {
    if (isAssessmentAgreement(row)) return assessmentLabel;
    return row.program_name || '—';
};

const AgreementRowActions: React.FC<{
    row: ClientAgreementRow;
    rowKey: string;
    busy: boolean;
    handleAction: PdfAgreementsTableProps['handleAction'];
}> = ({ row, rowKey, busy, handleAction }) => {
    const { translate } = useTranslate();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const signed = isAgreementSigned(row);
    const canView = isAssessmentAgreement(row) || row.application_id != null;

    const closeMenu = () => setAnchorEl(null);

    const handleOpen = (event: MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const run = (action: ACTIONS) => {
        closeMenu();
        handleAction(action, row, rowKey);
    };

    return (
        <>
            <IconButton
                size="small"
                onClick={handleOpen}
                aria-label={translate('actions')}
                disabled={busy}
            >
                {busy ? <CircularProgress size={18} /> : <MoreVert fontSize="small" />}
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={closeMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { minWidth: 180, mt: 0.5 } } }}
            >
                <MenuItem disabled={!canResendAgreement(row)} onClick={() => run(ACTIONS.RESEND)}>
                    <ListItemIcon>
                        <ForwardToInboxOutlined fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{translate('sendAgreementReminder')}</ListItemText>
                </MenuItem>
                <MenuItem disabled={row.application_id == null} onClick={() => run(ACTIONS.EDIT)}>
                    <ListItemIcon>
                        <EditOutlined fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{translate('edit')}</ListItemText>
                </MenuItem>
                <MenuItem disabled={!canView} onClick={() => run(ACTIONS.SIGN)}>
                    <ListItemIcon>
                        <VisibilityOutlined fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{translate('view')}</ListItemText>
                </MenuItem>
                <MenuItem disabled={!signed} onClick={() => run(ACTIONS.DOWNLOAD)}>
                    <ListItemIcon>
                        <DownloadOutlined fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{translate('download')}</ListItemText>
                </MenuItem>
                <MenuItem
                    disabled={!canDeleteAgreement(row)}
                    onClick={() => run(ACTIONS.DELETE)}
                    sx={{ color: 'error.main' }}
                >
                    <ListItemIcon>
                        <DeleteOutlined fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText>{translate('delete')}</ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
};

/** PDF retainer list (`GET /users/:id/agreements`) used when template retainers are off. */
export const PdfAgreementsTable: React.FC<PdfAgreementsTableProps> = ({
    rows,
    loading,
    busyKey,
    handleAction,
}) => {
    const { translate } = useTranslate();

    if (loading && rows.length === 0) {
        return <TableSkeleton />;
    }

    if (rows.length === 0) {
        return (
            <Result
                title={translate('noAgreementsYet')}
                description={translate('noAgreementsYetDescription')}
            />
        );
    }

    return (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={headCellSx}>{translate('service')}</TableCell>
                        <TableCell sx={headCellSx} align="right">
                            {translate('amount')}
                        </TableCell>
                        <TableCell sx={headCellSx}>{translate('dateSigned')}</TableCell>
                        <TableCell sx={headCellSx} align="right">
                            {translate('actions')}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, index) => {
                        const rowKey = clientAgreementRowKey(row, index);
                        const openMilestones = canOpenAgreementMilestones(row);
                        const signedOn = row.candidate_signed_date
                            ? formatDate(String(row.candidate_signed_date))
                            : null;

                        return (
                            <TableRow
                                key={rowKey}
                                hover={openMilestones}
                                onClick={
                                    openMilestones
                                        ? () => handleAction(ACTIONS.VIEW, row, rowKey)
                                        : undefined
                                }
                                sx={{ cursor: openMilestones ? 'pointer' : 'default' }}
                            >
                                <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {serviceLabel(row, translate('assessment'))}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                    {row.value != null && row.value !== ''
                                        ? formatNumber(row.value)
                                        : '—'}
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    {signedOn || translate('retainerStatusUnsigned')}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    onClick={(event) => event.stopPropagation()}
                                >
                                    <AgreementRowActions
                                        row={row}
                                        rowKey={rowKey}
                                        busy={busyKey === rowKey}
                                        handleAction={handleAction}
                                    />
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};
